import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn, rejectReasonLabel } from './model/derive';
import { formatDue, tzShortName } from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';
import { Modal, ModalButton } from './Modal';
import { uploadAttachments } from './FileDrop';
import TdButton from './Button';
import PeoplePicker from './PeoplePicker';
import MemberCard from './MemberCard';
import { litClass } from './useInboxFocus';
import {
  CommentText,
  MentionTextArea,
  Reactions,
  mentionIdsIn,
  mentionable,
} from './CommentExtras';

/**
 * 태스크 댓글 (MVP §7). 채팅이 아니라 태스크에 남는 기록이다.
 * - 질문은 받는 사람 1명을 꼭 지정한다 (응답 시간을 잴 수 있게)
 * - 팀 전체에 묻는 건 일반 댓글
 * - 작성자는 자기 일반 댓글 · 질문 · 막힘 · 답글을 고치거나 지울 수 있다 (반려 · 되돌림 사유는 그대로).
 *   지우면 자리만 남는다 ("This comment was deleted") — 답글과 응답 시간 기록은 그대로
 * - 사진 첨부 (이미지 4장까지)
 * - @멘션 · 이모지 반응 (CommentExtras)
 */

const reasonLabel = (key) => rejectReasonLabel(key) ?? key;

/** 댓글 종류 이름 — Task Flows typeLabel */
const typeLabel = (c, memberById) => {
  const target = memberById.get(c.targetUserId)?.name;
  if (c.type === 'blocker') {
    // 도움 요청은 여러 명일 수 있다 — "Blocker · help requested from A, B"
    const ids = c.targetUserIds?.length ? c.targetUserIds : c.targetUserId ? [c.targetUserId] : [];
    const names = ids
      .map((id) => memberById.get(id)?.name ?? (id === 'npc' ? 'Team lead Kim' : null))
      .filter(Boolean);
    return names.length ? `Blocker · help requested from ${names.join(', ')}` : 'Blocker';
  }
  if (c.type === 'question') return `Question to ${target ?? 'Team lead Kim'}`;
  if (c.type === 'rejection') return `Rejection reason · ${reasonLabel(c.reason)}`;
  if (c.type === 'return') return `Return reason · ${reasonLabel(c.reason)}`;
  if (c.type === 'general') return 'General';
  return null;
};

const PHOTO_MAX = 4;
const PHOTO_BYTES = 20 * 1024 * 1024;
const EDITABLE_TYPES = ['general', 'question', 'blocker', 'reply'];

/** 댓글에 붙일 사진 고르기 — 이미지만 4장 · 한 장 20MB 까지. 고른 사진은 작은 미리보기로 */
const PhotoPicker = ({ photos, onChange, disabled }) => {
  const ref = useRef(null);
  const [error, setError] = useState('');
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = photos.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);
  const add = (list) => {
    const picked = Array.from(list ?? []).filter((f) => f.type.startsWith('image/'));
    const ok = picked.filter((f) => f.size <= PHOTO_BYTES);
    const next = [...photos, ...ok];
    setError(
      ok.length < picked.length
        ? 'Each photo can be up to 20 MB.'
        : next.length > PHOTO_MAX
          ? `Up to ${PHOTO_MAX} photos.`
          : '',
    );
    onChange(next.slice(0, PHOTO_MAX));
  };
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {previews.map((url, i) => (
        <span key={url} className='relative h-14 w-14 overflow-hidden rounded-lg border border-green-deep03/[0.12]'>
          <img src={url} alt='' className='h-full w-full object-cover' />
          <button
            type='button'
            aria-label={`Remove photo ${i + 1}`}
            disabled={disabled}
            onClick={() => onChange(photos.filter((_, j) => j !== i))}
            className='absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-grayscale-gray09/70 text-[12px] leading-none text-grayscale-gray00'
          >
            ×
          </button>
        </span>
      ))}
      {photos.length < PHOTO_MAX && (
        <button
          type='button'
          disabled={disabled}
          onClick={() => ref.current?.click()}
          className='flex h-8 items-center gap-1 rounded-lg px-2 text-[13px] text-text-secondary hover:bg-grayscale-gray01 hover:text-text-primary disabled:opacity-50'
        >
          <PhotoIcon />
          Add photo
        </button>
      )}
      <input
        ref={ref}
        type='file'
        accept='image/*'
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      {error && <span className='text-[12px] text-functional-failed04'>{error}</span>}
    </div>
  );
};

const PhotoIcon = () => (
  <svg aria-hidden viewBox='0 0 16 16' className='h-4 w-4' fill='none' stroke='currentColor' strokeWidth='1.2'>
    <rect x='2' y='3' width='12' height='10' rx='2' />
    <circle cx='6' cy='7' r='1.2' />
    <path d='M2.5 12l3.5-3.5 2.5 2.5 2-2 3 3' strokeLinejoin='round' />
  </svg>
);

/** 고른 사진을 올리고 댓글에 붙일 첨부로 — 실제 보드는 S3, 목 보드는 브라우저 임시 주소 */
const usePhotoUpload = () => {
  const { live, slug } = useWorkboardCtx();
  const [uploading, setUploading] = useState(false);
  const upload = async (photos) => {
    if (!photos.length) return [];
    setUploading(true);
    try {
      return await uploadAttachments({ live, slug, purpose: 'comment' }, photos);
    } finally {
      setUploading(false);
    }
  };
  return [uploading, upload];
};

/** 댓글 사진 — 누르면 원본을 새 탭으로 */
const Photos = ({ files }) =>
  files?.length ? (
    <div className='flex flex-wrap gap-2'>
      {files.map((f, i) =>
        f.url ? (
          <a
            // eslint-disable-next-line react/no-array-index-key
            key={`${f.name}-${i}`}
            href={f.url}
            target='_blank'
            rel='noopener noreferrer'
            className='block h-28 w-28 overflow-hidden rounded-lg border border-green-deep03/[0.12] bg-grayscale-gray01'
          >
            <img src={f.url} alt={f.name} className='h-full w-full object-cover' />
          </a>
        ) : null,
      )}
    </div>
  ) : null;

const CommentBody = ({ c }) => {
  const { memberById, displayTz, now, data, dispatch, readOnly: ro, closed, actionsLocked } =
    useWorkboardCtx();
  const readOnly = ro || closed;
  const label = typeLabel(c, memberById);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleted = !!c.deletedAt;
  // 내 댓글 — 반려 · 되돌림 사유가 아니고, 서버 id 를 받은 뒤에만 고치거나 지운다
  const mine =
    !readOnly &&
    !actionsLocked &&
    !deleted &&
    c.authorId === data.viewerId &&
    EDITABLE_TYPES.includes(c.type) &&
    !/^c-\d+$/.test(c.id);
  const saveEdit = () => {
    const body = draft.trim();
    if (!body && !c.files?.length) return;
    dispatch({
      type: 'editComment',
      commentId: c.id,
      comment: { body, mentionIds: mentionIdsIn(body, mentionable(memberById, data.viewerId, now)) },
      at: now.toISOString(),
    });
    setEditing(false);
  };
  const remove = () => {
    dispatch({ type: 'deleteComment', commentId: c.id, at: now.toISOString() });
    setConfirmDelete(false);
  };
  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px] leading-[140%]'>
        {/* 이름을 누르면 팀원 카드 (사진 · 상태 · 작업 시간) */}
        <span className='font-medium text-text-primary'>
          <MemberCard
            memberId={c.authorId}
            variant='plain'
            align='left'
            fallback={c.authorId === 'npc' ? 'Team lead Kim' : ''}
          />
        </span>
        <span className='text-text-secondary'>
          {formatDue(c.createdAt, displayTz)} {tzShortName(displayTz, now)}
        </span>
        {label && (
          <span
            className={cn(
              c.type === 'general'
                ? 'rounded-md bg-grayscale-gray01 px-1.5 text-[12px] text-text-secondary'
                : c.type === 'rejection' || c.type === 'return'
                  ? 'font-medium text-functional-failed03'
                  : c.type === 'question'
                    ? // 질문 — 누구에게 물었는지 눈에 띄게 (받는 사람이 답해야 한다)
                      'rounded-md bg-surface-green10 px-1.5 text-[12px] font-medium text-green-deep04 shadow-[inset_0_0_0_1px_#1dc37b]'
                    : 'text-text-secondary',
            )}
          >
            {label}
          </span>
        )}
        {c.resolvedAt && (
          <span className='rounded-md bg-functional-success01 px-1.5 text-[12px] font-medium text-functional-success04'>
            Resolved
          </span>
        )}
        {c.editedAt && !deleted && (
          <span
            className='text-[12px] text-text-tertiary'
            title={`Edited ${formatDue(c.editedAt, displayTz)} ${tzShortName(displayTz, now)}`}
          >
            (edited)
          </span>
        )}
        {mine && !editing && (
          <span className='ml-auto flex gap-1'>
            <button
              type='button'
              onClick={() => {
                setDraft(c.body);
                setEditing(true);
              }}
              className='rounded px-1 text-[12px] text-text-secondary hover:text-text-primary'
            >
              Edit
            </button>
            <button
              type='button'
              onClick={() => setConfirmDelete(true)}
              className='rounded px-1 text-[12px] text-text-secondary hover:text-functional-failed03'
            >
              Delete
            </button>
          </span>
        )}
      </div>
      {deleted ? (
        <p className='text-[14px] italic leading-[150%] text-text-tertiary'>This comment was deleted.</p>
      ) : editing ? (
        <div className='flex flex-col gap-2'>
          <MentionTextArea autoFocus value={draft} onChange={setDraft} className='min-h-[64px]' />
          <div className='flex justify-end gap-2'>
            <ModalButton onClick={() => setEditing(false)}>Cancel</ModalButton>
            <ModalButton
              primary
              disabled={(!draft.trim() && !c.files?.length) || draft.trim() === c.body}
              onClick={saveEdit}
            >
              Save
            </ModalButton>
          </div>
        </div>
      ) : (
        <>
          {c.detail && <span className='text-[13px] text-text-secondary'>{c.detail}</span>}
          {c.body && (
            <div className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary'>
              <CommentText c={c} />
            </div>
          )}
          <Photos files={c.files} />
        </>
      )}
      {confirmDelete && (
        <Modal
          title='Delete this comment?'
          subtitle='Your teammates will see “This comment was deleted.” Replies stay.'
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <ModalButton onClick={() => setConfirmDelete(false)}>Cancel</ModalButton>
              <TdButton variant='danger' onClick={remove}>
                Delete
              </TdButton>
            </>
          }
        />
      )}
      {!deleted && <Reactions c={c} />}
    </div>
  );
};

/** 수정 요청을 받은 담당자에게 — 반려 사유 댓글 위에 한 번 띄우는 안내 */
const ReworkGuide = ({ onDismiss }) => (
  <div
    role='note'
    className='mb-3 flex flex-wrap items-start gap-x-4 gap-y-2 rounded-xl bg-grayscale-gray09 px-4 py-3 text-[13px] leading-[150%] text-grayscale-gray00 shadow-popup-01'
  >
    <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
      <span className='font-medium'>Changes requested</span>
      <span className='text-grayscale-gray03'>
        Fix the items marked ✗ and what the reviewer wrote below, then use Submit new version at
        the top. Reply here if anything is unclear.
      </span>
    </span>
    <button
      type='button'
      onClick={onDismiss}
      className='h-8 shrink-0 rounded-lg bg-grayscale-gray00/15 px-3 text-[13px] font-medium hover:bg-grayscale-gray00/25'
    >
      Got it
    </button>
  </div>
);

const Thread = ({ c, replies, taskId, lit, litId, guide }) => {
  const { dispatch, data, memberById, now, readOnly: ro, closed } = useWorkboardCtx();
  const readOnly = ro || closed;
  // 질문을 받은 사람은 답글 칸이 처음부터 열려 있다 (W4_Comments_Responder)
  const askedMe = c.type === 'question' && c.targetUserId === data.viewerId && !c.resolvedAt;
  const [replying, setReplying] = useState(askedMe && replies.length === 0);
  useEffect(() => {
    if (lit && !readOnly && !c.resolvedAt) setReplying(true);
  }, [lit, readOnly, c.resolvedAt]);
  // 질문을 단 사람 — 답글이 달리면 Mark resolved
  const canResolve =
    !readOnly &&
    c.type === 'question' &&
    !c.resolvedAt &&
    c.authorId === data.viewerId &&
    replies.length > 0;
  const resolve = () =>
    dispatch({ type: 'resolveComment', commentId: c.id, at: now.toISOString() });
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, upload] = usePhotoUpload();
  const [failed, setFailed] = useState('');
  const posting = useRef(false);
  const post = async () => {
    // 보내는 중에 또 누르면 무시 — 같은 답글이 두 번 달리지 않게
    if (posting.current) return;
    posting.current = true;
    let files;
    try {
      files = await upload(photos);
    } catch {
      setFailed('Couldn’t upload the photos. Try again.');
      posting.current = false;
      return;
    }
    setFailed('');
    dispatch({
      type: 'addComment',
      comment: {
        id: `c-${Date.now()}`,
        taskId,
        authorId: data.viewerId,
        type: 'reply',
        body: text.trim(),
        createdAt: now.toISOString(),
        parentId: c.id,
        mentionIds: mentionIdsIn(text, mentionable(memberById, data.viewerId, now)),
        ...(files.length ? { files } : {}),
      },
    });
    setText('');
    setPhotos([]);
    setReplying(false);
    posting.current = false;
  };
  return (
    <li
      id={`comment-${c.id}`}
      className='scroll-mt-24 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-4 py-3.5 target:ring-2 target:ring-green-deep03/40'
    >
      {guide && <ReworkGuide onDismiss={guide} />}
      {/* 강조는 Inbox 에서 고른 댓글 하나에만 (스레드 전체가 아니라) */}
      <div
        data-inbox-lit={litId === c.id || undefined}
        className={cn(
          '-mx-2 -my-1 rounded-lg px-2 py-1 transition-[background-color,box-shadow] duration-300',
          litClass(litId === c.id),
        )}
      >
        <CommentBody c={c} />
      </div>
      {replies.length > 0 && (
        <ul className='mt-3 flex flex-col gap-3 border-l-2 border-green-deep03/[0.12] pl-4'>
          {replies.map((r) => (
            <li
              key={r.id}
              id={`comment-${r.id}`}
              data-inbox-lit={litId === r.id || undefined}
              className={cn(
                '-mx-2 -my-1 scroll-mt-24 rounded-lg px-2 py-1 transition-[background-color,box-shadow] duration-300 target:bg-green-deep03/[0.06]',
                litClass(litId === r.id),
              )}
            >
              <CommentBody c={r} />
            </li>
          ))}
        </ul>
      )}
      {replying ? (
        <div className='mt-3 flex flex-col gap-2'>
          <MentionTextArea
            autoFocus={!askedMe}
            placeholder='Write a reply · type @ to mention'
            value={text}
            onChange={setText}
            className='min-h-[64px]'
          />
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <PhotoPicker photos={photos} onChange={setPhotos} disabled={uploading} />
            <div className='ml-auto flex gap-2'>
              {!askedMe && <ModalButton onClick={() => setReplying(false)}>Cancel</ModalButton>}
              <ModalButton
                primary
                disabled={(!text.trim() && !photos.length) || uploading}
                onClick={post}
              >
                {uploading ? 'Uploading…' : 'Post reply'}
              </ModalButton>
            </div>
          </div>
          {failed && <p className='text-[12px] text-functional-failed04'>{failed}</p>}
        </div>
      ) : readOnly || c.resolvedAt ? null : (
        <div className='mt-2 flex flex-wrap gap-1'>
          <TdButton
            variant='ghost'
            size='s'
            onClick={() => setReplying(true)}
            className='px-2 font-normal'
          >
            Reply
          </TdButton>
          {canResolve && (
            <TdButton
              variant='ghost'
              size='s'
              onClick={resolve}
              className='px-2 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
            >
              Mark resolved
            </TdButton>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * @param focusId Inbox 에서 온 댓글 — 강조하고 답글 칸을 연다
 * @param reworkId 수정 요청을 받은 담당자가 볼 반려 사유 댓글 — 들어올 때 그 댓글로 내린다
 * @param onGuideDismiss 있으면 반려 사유 댓글 위에 안내를 띄운다 (Got it 으로 닫는다)
 */
const Comments = ({
  task,
  focusId = null,
  reworkId = null,
  onGuideDismiss = null,
  embedded = false,
  showThreads = true,
}) => {
  const {
    data,
    dispatch,
    memberById,
    now,
    readOnly: ro,
    closed,
    closedTip,
    actionsLocked,
    lockTip,
  } = useWorkboardCtx();
  const readOnly = ro || closed;
  const [type, setType] = useState('general');
  const [target, setTarget] = useState('');
  const [text, setText] = useState('');

  const all = (data.comments ?? []).filter((c) => c.taskId === task.id);
  const focused = focusId ? all.find((c) => c.id === focusId) : null;
  const litRoot = focused ? (focused.parentId ?? focused.id) : null;
  // 알림에서 댓글로 왔으면(#comment-id) 그 댓글로 내린다
  const { hash } = useLocation();
  const found = hash.startsWith('#comment-') && all.some((c) => `#comment-${c.id}` === hash);
  // 수정 요청 태스크에 들어오면 반려 사유 댓글로 한 번 내린다 (다른 댓글로 온 경우는 그쪽이 먼저)
  const reworkScrolled = useRef(null);
  useEffect(() => {
    if (!reworkId || focusId || found || reworkScrolled.current === reworkId) return undefined;
    reworkScrolled.current = reworkId;
    const timer = window.setTimeout(
      () =>
        document
          .getElementById(`comment-${reworkId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      120,
    );
    return () => window.clearTimeout(timer);
  }, [reworkId, focusId, found]);
  useEffect(() => {
    if (!found) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [found, hash]);
  const roots = all.filter((c) => !c.parentId);
  const [photos, setPhotos] = useState([]);
  const [uploading, upload] = usePhotoUpload();
  const [failed, setFailed] = useState('');
  // 사진만 올리는 것은 일반 댓글에서만 (질문은 글이 있어야 한다)
  const canPost =
    !actionsLocked &&
    !uploading &&
    (text.trim() || (type === 'general' && photos.length)) &&
    (type === 'general' || target);

  const posting = useRef(false);
  const post = async () => {
    // 보내는 중에 또 누르면 무시 — 같은 댓글이 두 번 달리지 않게
    if (posting.current) return;
    posting.current = true;
    let files;
    try {
      files = await upload(photos);
    } catch {
      setFailed('Couldn’t upload the photos. Try again.');
      posting.current = false;
      return;
    }
    setFailed('');
    dispatch({
      type: 'addComment',
      comment: {
        id: `c-${Date.now()}`,
        taskId: task.id,
        authorId: data.viewerId,
        type,
        targetUserId: type === 'question' ? target : null,
        body: text.trim(),
        createdAt: now.toISOString(),
        parentId: null,
        mentionIds: mentionIdsIn(text, mentionable(memberById, data.viewerId, now)),
        ...(files.length ? { files } : {}),
      },
    });
    setText('');
    setTarget('');
    setPhotos([]);
    posting.current = false;
  };

  const Wrap = embedded ? React.Fragment : 'section';
  return (
    <Wrap {...(embedded ? {} : { className: 'flex flex-col gap-3' })}>
      {!embedded && (
        <h2 className='flex items-baseline gap-2 text-[16px] font-medium leading-[150%] text-text-primary'>
          Comments
          <span className='text-[13px] font-normal text-text-secondary'>{all.length}</span>
        </h2>
      )}
      {showThreads && roots.length > 0 && (
        <ul className='flex flex-col gap-3'>
          {roots.map((c) => (
            <Thread
              key={c.id}
              c={c}
              taskId={task.id}
              replies={all.filter((r) => r.parentId === c.id)}
              lit={litRoot === c.id}
              litId={focused?.id ?? null}
              guide={c.id === reworkId ? onGuideDismiss : null}
            />
          ))}
        </ul>
      )}

      {/* 프로젝트를 마쳤으면 — 댓글 칸 대신 안내 */}
      {!ro && closed && (
        <p className='rounded-xl bg-grayscale-gray01 px-4 py-3 text-[13px] leading-[150%] text-text-secondary'>
          {closedTip}
        </p>
      )}
      {/* 스태프 열람(읽기 전용)이면 댓글을 쓰지 않는다 */}
      {!readOnly && (
        <div className='flex flex-col gap-3 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 p-4'>
          {/* 유형 탭과 받는 사람을 한 줄에 — 질문이면 받는 사람 1명 필수 (MVP §7) */}
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
            <div
              role='tablist'
              aria-label='Comment type'
              className='flex w-fit gap-0.5 rounded-lg bg-surface-green10 p-0.5'
            >
              {[
                ['question', 'Question'],
                ['general', 'General'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type='button'
                  role='tab'
                  aria-selected={type === key}
                  onClick={() => setType(key)}
                  className={cn(
                    'h-8 rounded-md px-3 text-[13px]',
                    type === key
                      ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
                      : 'text-text-secondary',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {type === 'question' && (
              <label className='flex items-center gap-2 text-[13px] text-text-secondary'>
                <span>
                  To<span className='text-functional-failed03'>*</span>
                </span>
                <PeoplePicker value={target} onChange={setTarget} />
              </label>
            )}
          </div>
          <MentionTextArea
            disabled={actionsLocked}
            title={actionsLocked ? lockTip : undefined}
            placeholder={actionsLocked ? lockTip : 'Write a comment · type @ to mention'}
            value={text}
            onChange={setText}
            className='min-h-[72px]'
          />
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <PhotoPicker
              photos={photos}
              onChange={setPhotos}
              disabled={actionsLocked || uploading}
            />
            <ModalButton primary disabled={!canPost} onClick={post} className='ml-auto'>
              {uploading ? 'Uploading…' : 'Post'}
            </ModalButton>
          </div>
          {failed && <p className='text-[12px] text-functional-failed04'>{failed}</p>}
        </div>
      )}
    </Wrap>
  );
};

export default Comments;
